import React from 'react';
import styled, { css } from 'styled-components';
import LazyImage from '../lazy-image';
import { getFluidGatsbyImage } from '../../utils/gatsby-storyblok-image-fixed';
import RichtextParser from '../../utils/richtext-parser';
import CtaWithArrow from '../cta-with-arrow';
import parseStoryblokLink from '../../utils/parseStoryblokLink';
import mq from '../../utils/mq';

const ContentCardGridItemContainer = styled.div<{ $theme?: 'white' | 'grey' }>`
  display: flex;
  flex-direction: column;
  gap: 44px;
  padding: 46px;
  border-radius: var(--Radius-Radius-3, 16px);
  background: ${({ $theme }) => ($theme === 'white' ? '#f8f8f8' : '#fff')};
  @media (max-width: 1100px) {
    padding: 32px;
    gap: 32px;
  }
  ${mq.mobile(css`
    padding: 16px;
    gap: 32px;
  `)};
`;

const ContentCardGridItemImage = styled.div<{ $theme?: 'white' | 'grey' }>`
  aspect-ratio: 3/2;
  width: 100%;
  overflow: hidden;
  position: relative;
  border-radius: var(--Radius-Radius-3, 16px);
  background: ${({ $theme }) => ($theme === 'white' ? '#fff' : '#f8f8f8')};
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: center;
  justify-content: space-between;
  flex-grow: 1;
  ${mq.mobile(css`
    text-align: left;
    padding: 0 16px;
  `)};
`;

const TextContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: center;
  ${mq.mobile(css`
    text-align: left;
    padding: 0 16px;
  `)};
`;

const Title = styled.h3`
  margin-bottom: 12px;
  color: #161616;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: 0px;
  text-align: inherit;
  ${mq.mobile(css`
    margin-bottom: 0;
  `)};
`;

const BodyContent = styled.div`
  color: #666;
  text-align: inherit;
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 160%;
  letter-spacing: -0.18px;
  text-wrap: balance;
  ${mq.mobile(css`
    text-wrap: pretty;
  `)};
`;

const CtaContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 32px;
  ${mq.mobile(css`
    padding-top: 16px;
    justify-content: flex-start;
    padding-bottom: 16px;
  `)};
`;

export default function ContentCardGridItem({
  asset,
  title,
  body,
  ctaLink,
  ctaText,
  theme
}: {
  asset: any;
  title: string;
  body: any;
  ctaLink: any;
  ctaText: string;
  theme?: 'white' | 'grey';
}) {
  return (
    <ContentCardGridItemContainer $theme={theme}>
      <ContentCardGridItemImage $theme={theme}>
        <LazyImage
          fluid={getFluidGatsbyImage(asset)}
          width="100%"
          height="100%"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </ContentCardGridItemImage>
      <TextContent>
        <TextContentContainer>
          <Title>{title}</Title>
          <BodyContent
            dangerouslySetInnerHTML={{ __html: RichtextParser(body) }}
          />
        </TextContentContainer>
        {ctaLink && ctaText && (
          <CtaContainer>
            <CtaWithArrow link={parseStoryblokLink(ctaLink)} label={ctaText} />
          </CtaContainer>
        )}
      </TextContent>
    </ContentCardGridItemContainer>
  );
}
